import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Typography,
  Grid,
  Alert,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Switch,
  FormControlLabel,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import axios from '../../api/axios';
import FileUploader from './FileUploader';

const DocumentsManager = () => {
  const [documents, setDocuments] = useState([]);
  const [open, setOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    fileUrl: '',
    fileType: 'pdf',
    category: 'other',
    order: 0,
    isPublished: true,
  });

  const categories = {
    federal: 'Федеральные',
    regional: 'Региональные',
    local: 'Локальные акты',
    regulations: 'Нормативные документы',
    reports: 'Отчеты',
    programs: 'Программы',
    forms: 'Формы и бланки',
    other: 'Другое',
  };

  const fileTypes = {
    pdf: 'PDF',
    doc: 'DOC',
    docx: 'DOCX',
    xls: 'XLS',
    xlsx: 'XLSX',
    jpg: 'JPG',
    png: 'PNG',
    link: 'Ссылка',
    other: 'Другое',
  };

  useEffect(() => {
    fetchDocuments();
  }, []);

  const fetchDocuments = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get('/documents', {
        headers: { Authorization: `Bearer ${token}` },
      });
      setDocuments(response.data);
    } catch (error) {
      console.error('Error fetching documents:', error);
      setError('Ошибка загрузки документов');
    }
  };

  const handleOpen = (document = null) => {
    if (document) {
      setEditingId(document._id);
      setFormData({
        title: document.title,
        description: document.description,
        fileUrl: document.fileUrl,
        fileType: document.fileType,
        category: document.category,
        order: document.order,
        isPublished: document.isPublished,
      });
    } else {
      setEditingId(null);
      setFormData({
        title: '',
        description: '',
        fileUrl: '',
        fileType: 'pdf',
        category: 'other',
        order: 0,
        isPublished: true,
      });
    }
    setOpen(true);
    setError('');
    setSuccess('');
  };

  const handleClose = () => {
    setOpen(false);
    setEditingId(null);
  };

  const handleSubmit = async () => {
    if (!formData.title || !formData.fileUrl) {
      setError('Заполните обязательные поля');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      if (editingId) {
        await axios.put(`/documents/${editingId}`, formData, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setSuccess('Документ успешно обновлен');
      } else {
        await axios.post('/documents', formData, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setSuccess('Документ успешно создан');
      }
      handleClose();
      fetchDocuments();
      setTimeout(() => setSuccess(''), 3000);
    } catch (error) {
      console.error('Error saving document:', error);
      setError('Ошибка при сохранении документа');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Вы уверены, что хотите удалить этот документ?')) {
      return;
    }
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`/documents/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setSuccess('Документ успешно удален');
      fetchDocuments();
      setTimeout(() => setSuccess(''), 3000);
    } catch (error) {
      console.error('Error deleting document:', error);
      setError('Ошибка при удалении документа');
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5" fontWeight={600}>
          Управление документами
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => handleOpen()}
        >
          Добавить документ
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Название</TableCell>
              <TableCell>Категория</TableCell>
              <TableCell>Тип файла</TableCell>
              <TableCell>Порядок</TableCell>
              <TableCell>Опубликован</TableCell>
              <TableCell align="right">Действия</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {documents.map((document) => (
              <TableRow key={document._id}>
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <InsertDriveFileIcon color="primary" />
                    {document.title}
                  </Box>
                </TableCell>
                <TableCell>{categories[document.category]}</TableCell>
                <TableCell>{fileTypes[document.fileType]}</TableCell>
                <TableCell>{document.order}</TableCell>
                <TableCell>{document.isPublished ? '✓' : '✗'}</TableCell>
                <TableCell align="right">
                  <IconButton
                    color="primary"
                    onClick={() => handleOpen(document)}
                  >
                    <EditIcon />
                  </IconButton>
                  <IconButton
                    color="error"
                    onClick={() => handleDelete(document._id)}
                  >
                    <DeleteIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingId ? 'Редактировать документ' : 'Добавить документ'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Название документа"
                  value={formData.title}
                  onChange={(e) =>
                    setFormData({ ...formData, title: e.target.value })
                  }
                  required
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Описание"
                  value={formData.description}
                  onChange={(e) =>
                    setFormData({ ...formData, description: e.target.value })
                  }
                  multiline
                  rows={2}
                />
              </Grid>
              
              <Grid item xs={12}>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Файл документа
                </Typography>
                <FileUploader
                  onUploadSuccess={(url) => setFormData({ ...formData, fileUrl: url })}
                  currentFile={formData.fileUrl}
                  fileType={formData.fileType === 'link' ? 'other' : formData.fileType}
                  inputId="document-file-upload-input"
                  maxSizeMB={100}
                />
                {!formData.fileUrl && (
                  <TextField
                    fullWidth
                    label="Или введите URL файла"
                    value={formData.fileUrl}
                    onChange={(e) =>
                      setFormData({ ...formData, fileUrl: e.target.value })
                    }
                    placeholder="https://example.com/document.pdf"
                    sx={{ mt: 2 }}
                  />
                )}
              </Grid>

              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>Категория</InputLabel>
                  <Select
                    value={formData.category}
                    label="Категория"
                    onChange={(e) =>
                      setFormData({ ...formData, category: e.target.value })
                    }
                  >
                    {Object.entries(categories).map(([key, label]) => (
                      <MenuItem key={key} value={key}>
                        {label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>Тип файла</InputLabel>
                  <Select
                    value={formData.fileType}
                    label="Тип файла"
                    onChange={(e) =>
                      setFormData({ ...formData, fileType: e.target.value })
                    }
                  >
                    {Object.entries(fileTypes).map(([key, label]) => (
                      <MenuItem key={key} value={key}>
                        {label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  type="number"
                  label="Порядок отображения"
                  value={formData.order}
                  onChange={(e) =>
                    setFormData({ ...formData, order: parseInt(e.target.value) })
                  }
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.isPublished}
                      onChange={(e) =>
                        setFormData({ ...formData, isPublished: e.target.checked })
                      }
                    />
                  }
                  label="Опубликован"
                />
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Отмена</Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={!formData.title || !formData.fileUrl}
          >
            {editingId ? 'Сохранить' : 'Создать'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default DocumentsManager;
